// hooks/useVariantDrawer.ts
import { useState } from 'react';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { VariantComment, VariantDetails, initialComments } from '../types';
import {
  useCommentVariant,
  useDeleteVariantComment,
  useFirstValidateVariant,
  useGetSelectedVariants,
  useGetVariantComments,
  useRejectVariant,
  useRemoveVariants,
} from '@/api/result/result';
import { getErrorMessage } from '@/utils/getErrorMessage';

export function useVariantDrawer(
  outputID: string,
  personStatus?: string,
  currentCursor?: string | null,
) {
  const userType =
    personStatus === 'User 1' ? 'first-validate' : 'second-validate'; // For 1st,2nd validator

  const [drawerHeight, setDrawerHeight] = useState(90);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);

  const [selectedVariation, setSelectedVariation] = useState<string | null>(
    null,
  );
  const [variantDetails, setVariantDetails] = useState<VariantDetails>();
  const [newComment, setNewComment] = useState('');
  const [, setComments] = useState<VariantComment[]>(initialComments);

  const validateVariant = useFirstValidateVariant();
  const rejectVariant = useRejectVariant();
  const commentVariant = useCommentVariant();
  const deleteVariantComment = useDeleteVariantComment();
  const removeVariants = useRemoveVariants();

  const { data: variantComments = [], refetch: refetchVariantComments } =
    useGetVariantComments(variantDetails?.selected_variant_id);

  const {
    data: selectedVariant,
    isLoading: dataLoading,
    refetch,
  } = useGetSelectedVariants(outputID, currentCursor || undefined);

  const onRemoveVariant = async () => {
    if (selectedRows.length <= 0) {
      enqueueSnackbar('Please select atleast 1 variant', {
        variant: 'info',
      });
    } else {
      try {
        await removeVariants
          .mutateAsync({
            outputId: outputID!,
            variantIds: selectedRows as string[],
          })
          .then(() => {
            enqueueSnackbar('Variant successfully removed', {
              variant: 'success',
            });
          })
          .catch(() => {
            enqueueSnackbar('Failed to remove variant', {
              variant: 'error',
            });
          });
        refetch();
      } catch (ex) {
        enqueueSnackbar('Failed to remove variant', { variant: 'error' });
        refetch();
      }
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setNewComment('');
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onCommentVariant = async (
    id: string,
    type: 'general' | 'first_validation' | 'rejection' = 'general',
  ) => {
    if (!newComment.trim() || !selectedVariation) {
      enqueueSnackbar('Please write a comment first.', { variant: 'info' });
      return;
    }

    try {
      const res = await commentVariant.mutateAsync({
        id,
        comment: newComment.trim(),
        comment_type: type,
      });

      setComments((prev) => [res, ...prev]);

      enqueueSnackbar('Comment added successfully', {
        variant: 'success',
      });
      setNewComment('');
      refetchVariantComments();
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
  };

  // Delete a comment
  const handleDeleteComment = async (commentId: string) => {
    if (!variantDetails?.selected_variant_id) {
      enqueueSnackbar('Failed to retrieve variant ID', { variant: 'error' });
      return;
    }

    try {
      await deleteVariantComment.mutateAsync({
        id: variantDetails.selected_variant_id,
        commentId,
      });

      // Optimistic local state update
      setComments((prev) => prev.filter((c) => c.id !== commentId));

      enqueueSnackbar('Comment deleted successfully', {
        variant: 'success',
      });
      refetchVariantComments();
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
  };

  const handleRowClick = (params: GridRowParams) => {
    const variation = params.row.uploaded_variation;
    setSelectedVariation(variation);
    setVariantDetails(params.row);
    setDrawerOpen(true);
  };

  const onValidateVariant = async (id: string) => {
    try {
      const res = await validateVariant.mutateAsync({ id, userType });

      // success — use server message if present
      enqueueSnackbar(res?.detail || 'Variant validated successfully', {
        variant: 'success',
      });

      // optimistic UI tweak if you keep the details locally
      setVariantDetails((v) =>
        v ? { ...v, validation_status: 'first_validated' } : v,
      );

      // optionally refetch the list/drawer source
      // queryClient.invalidateQueries(['saved_variants', outputID]);
    } catch (error: unknown) {
      const err = error as AxiosError<{ detail?: string }>;

      const data = err?.response?.data;

      // backend might return a string or an array of strings
      let detail = '';

      if (typeof data === 'string') {
        detail = data;
      } else if (Array.isArray(data)) {
        detail = data.join(', ');
      } else {
        detail = data?.detail || '';
      }

      if (detail.toLowerCase().includes('cannot perform first validation')) {
        enqueueSnackbar('This variant is already validated.', {
          variant: 'info',
        });
        setVariantDetails((v) =>
          v ? { ...v, validation_status: 'first_validated' } : v,
        );
        return;
      }

      enqueueSnackbar(detail || 'Validation failed', { variant: 'error' });
    }
  };

  const onRejectVariant = async (id: string) => {
    try {
      const res = await rejectVariant.mutateAsync({ id });

      // success — use server message if present
      enqueueSnackbar(res?.detail || 'Variant validated successfully', {
        variant: 'success',
      });

      // optimistic UI tweak if you keep the details locally
      setVariantDetails((v) =>
        v ? { ...v, validation_status: 'first_validated' } : v,
      );

      // optionally refetch the list/drawer source
      // queryClient.invalidateQueries(['saved_variants', outputID]);
    } catch (error: unknown) {
      const err = error as AxiosError<{ detail?: string }>;
      const data = err?.response?.data;

      // backend might return a string or an array of strings
      let detail = '';

      if (typeof data === 'string') {
        detail = data;
      } else if (Array.isArray(data)) {
        detail = data.join(', ');
      } else {
        detail = data?.detail || '';
      }

      if (detail.toLowerCase().includes('cannot perform first validation')) {
        enqueueSnackbar('This variant is already validated.', {
          variant: 'info',
        });
        setVariantDetails((v) =>
          v ? { ...v, validation_status: 'first_validated' } : v,
        );
        return;
      }

      enqueueSnackbar(detail || 'Validation failed', { variant: 'error' });
    }
  };

  return {
    drawerHeight,
    setDrawerHeight,
    drawerOpen,
    setDrawerOpen,
    isDragging,
    setIsDragging,
    selectedVariation,
    variantDetails,
    newComment,
    setNewComment,
    handleCloseDrawer,
    handleMouseDown,
    onCommentVariant,
    handleDeleteComment,
    handleRowClick,
    variantComments,
    onValidateVariant,
    onRejectVariant,
    userType,
    selectedVariant,
    dataLoading,
    onRemoveVariant,
    setSelectedVariation,
    setVariantDetails,
    setSelectedRows,
  };
}
